import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { SafeAny } from '../../types/safe-any';

import {
  CustomerCodes,
  ExportOrdersBundlePayload,
  ExportOrdersBundleResponse,
  Note,
  OrderClient,
  OrderExportDetails,
  OrderExportPayload,
  SavedLists,
  SingleList,
  SingleListPayload,
  SingleListPUTApiResponse,
  singleOrderDetails,
  SingleOrderPayload,
} from './orders.model';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private apiUrl = `${environment.apiUrl}/orders`;

  constructor(private http: HttpClient) {}

  getOrderById(id: string, isEdit?: boolean): Observable<singleOrderDetails> {
    const isEditParam = isEdit ? '?isEdit=true' : '';

    return this.http.get<singleOrderDetails>(`${this.apiUrl}/${id}${isEditParam}`);
  }

  getClients(payload?: any): Observable<OrderClient[]> {
    return this.http.get<OrderClient[]>(`${environment.apiUrl}/clients`, { params: payload });
  }

  addOrderNote(orderId: number, note: string): Observable<Note> {
    return this.http.post<Note>(`${this.apiUrl}/${orderId}/notes`, { note });
  }

  updateOrder(orderId: number, order: SingleOrderPayload): Observable<any> {
    return this.http.put(`${this.apiUrl}/${orderId}`, order);
  }

  getOrders(payload: any): Observable<HttpResponse<singleOrderDetails[]>> {
    const cleanPayload: any = Object.fromEntries(
      Object.entries(payload).filter(([_, value]) => value !== null),
    );
    return this.http.get<singleOrderDetails[]>(`${this.apiUrl}`, {
      params: cleanPayload,
      observe: 'response',
    });
  }

  archiveOrder(orderId: string, isArchived: boolean): Observable<any> {
    return this.http.put(`${this.apiUrl}/${orderId}/status`, {
      status: isArchived ? 'UNARCHIVE' : 'ARCHIVE',
    });
  }

  deleteOrder(orderId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${orderId}`);
  }

  getPastDueOrders(payload: SafeAny): Observable<HttpResponse<singleOrderDetails[]>> {
    return this.http.get<singleOrderDetails[]>(`${this.apiUrl}`, {
      params: payload,
      observe: 'response',
    });
  }

  createOrder(payload: SafeAny): Observable<singleOrderDetails> {
    return this.http.post<singleOrderDetails>(`${this.apiUrl}`, payload);
  }

  getLists(params: {
    offset: number;
    limit: number;
    sort?: string;
  }): Observable<HttpResponse<SavedLists[]>> {
    let queryParams = new HttpParams()
      .set('offset', params.offset.toString())
      .set('limit', params.limit.toString());

    if (params.sort) {
      queryParams = queryParams.set('sort', params.sort);
    }

    return this.http.get<SavedLists[]>(`${environment.apiUrl}/orders-lists`, {
      params: queryParams,
      observe: 'response',
    });
  }

  getCustomerCodes(payload?: any): Observable<CustomerCodes> {
    return this.http.get<CustomerCodes>(`${environment.apiUrl}/clients/codes`, { params: payload });
  }

  getSingleList(id: number, payload?: any): Observable<SingleList> {
    return this.http.get<SingleList>(`${environment.apiUrl}/orders-lists/${id}`, {
      params: payload,
    });
  }

  updateSingleList(id: number, payload: SingleListPayload): Observable<SingleListPUTApiResponse> {
    return this.http.put<SingleListPUTApiResponse>(
      `${environment.apiUrl}/orders-lists/${id}`,
      payload,
    );
  }

  deleteList(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/orders-lists/${id}`);
  }

  exportList(id: number, format: 'csv' | 'pdf'): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/orders-lists/${id}/export`, {
      responseType: 'blob',
      params: { format },
    });
  }

  createSingleList(payload: SingleListPayload): Observable<SingleListPUTApiResponse> {
    return this.http.post<SingleListPUTApiResponse>(`${environment.apiUrl}/orders-lists`, payload);
  }

  getOrderExportDetails(orderId: string): Observable<OrderExportDetails> {
    return this.http.get<OrderExportDetails>(`${this.apiUrl}/${orderId}/export-details`);
  }

  exportOrder(orderId: number, payload: OrderExportPayload): Observable<any> {
    return this.http.post(`${this.apiUrl}/${orderId}/export`, payload);
  }

  exportOrdersBundle(payload: ExportOrdersBundlePayload): Observable<ExportOrdersBundleResponse> {
    return this.http.post<ExportOrdersBundleResponse>(
      `${environment.apiUrl}/orders/pdf-bundle`,
      payload,
    );
  }

  unlockOrder(orderId: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/${orderId}/unlock`, {});
  }
}
